import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "refundButton",
    "refundTypeFull",
    "refundTypeMinusFee",
    "refundTypePartial",
    "refundAmountContainer",
    "refundAmount",
    "errors",
    "cancelUpcomingLessons",
    "refundReason",
    "paymentOption",
    "forceRefundWithoutLesson",
    "paymentHeader",
    "fullRefundLabel",
  ];

  connect() {
    console.log("SchoolPaymentRefundQuickLookController connected");
    setTimeout(() => {
      this.validateForm();
    });
  }

  disconnect() {
    console.log("SchoolPaymentRefundQuickLookController disconnected");
  }

  async showQuickLook(event) {
    event.preventDefault();

    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
    const paymentToken = this.data.get("paymentTokenValue");

    const response = await fetch(
      `/schools/payments/${paymentToken}/quick-look/refund`,
      {
        method: "GET",
        cache: "no-store",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": csrfToken,
          Accept: "text/vnd.turbo-stream.html",
        },
      }
    );

    if (response.ok) {
      console.log("response.ok");
      console.log(response);

      const quickViewElement = document.querySelector(
        "#school-payment-quick-look"
      );
      quickViewElement.classList.remove("u-hidden");

      const responseBody = await response.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(responseBody, "text/html");

      quickViewElement.innerHTML = "";
      quickViewElement.appendChild(xmlDoc.body);
    } else {
      alert(
        "There was an error while trying to load the quick look, please try again later."
      );
    }
  }

  async openSplitPaymentRefundFlow(event) {
    event.preventDefault();

    const paymentId = event.params.paymentId;
    const paymentToken = this.data.get("paymentTokenValue");
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;

    const response = await fetch(
      `/schools/payments/${paymentToken}/quick-look/refund?payment_id=${paymentId}`,
      {
        method: "GET",
        cache: "no-store",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": csrfToken,
          Accept: "text/vnd.turbo-stream.html",
        },
      }
    );

    if (response.ok) {
      const quickViewElement = document.querySelector(
        "#school-payment-quick-look"
      );
      quickViewElement.classList.remove("u-hidden");

      const responseBody = await response.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(responseBody, "text/html");

      quickViewElement.innerHTML = "";
      quickViewElement.appendChild(xmlDoc.body);
    } else {
      alert(
        "There was an error while trying to load the quick look, please try again later."
      );
    }
  }

  async openPaymentDetails(event) {
    event.preventDefault();

    const paymentToken = this.data.get("paymentTokenValue");
    const paymentId = event.params.paymentId;
    const ledgerEntryId = event.params.ledgerEntryId;
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
    const query = new URLSearchParams();
    if (paymentId) query.set("payment_id", paymentId);
    if (ledgerEntryId) query.set("ledger_entry_id", ledgerEntryId);
    const response = await fetch(
      `/schools/payments/${paymentToken}/quick-look/details?${query.toString()}`,
      {
        method: "GET",
        cache: "no-store",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": csrfToken,
          Accept: "text/vnd.turbo-stream.html",
        },
      }
    );

    if (response.ok) {
      const quickViewElement = document.querySelector("#school-payment-quick-look");
      quickViewElement.classList.remove("u-hidden");
      const responseBody = await response.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(responseBody, "text/html");
      quickViewElement.innerHTML = "";
      quickViewElement.appendChild(xmlDoc.body);
    } else {
      alert("There was an error while trying to load the payment details, please try again later.");
    }
  }

  paymentChanged() {
    const selected = this.paymentOptionTargets.find((option) => option.checked);
    if (selected && this.hasPaymentHeaderTarget) {
      this.paymentHeaderTarget.innerHTML = selected.dataset.paymentHeaderHtml || "";
    }
    if (selected && this.hasFullRefundLabelTarget) {
      this.fullRefundLabelTarget.textContent = `Full refund (${selected.dataset.refundableAmountFormatted})`;
    }
    if (selected && this.hasRefundAmountTarget) {
      this.refundAmountTarget.placeholder = selected.dataset.refundableAmountDecimal || "";
    }
    this.validateForm();
  }

  closeQuickLook(event) {
    event.preventDefault();
    const quickViewElement = document.querySelector(
      "#school-payment-quick-look"
    );
    quickViewElement.classList.add("u-hidden");
  }

  async confirmRefund(event) {
    this.beginButtonLoading();

    const paymentToken = this.data.get("paymentTokenValue");
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
    const response = await fetch(
      `/schools/payments/${paymentToken}/quick-look/refund`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          Accept: "application/json",
        },
        body: JSON.stringify({
          refundType: this.refundTypeFullTarget.checked
            ? "full"
            : this.hasRefundTypeMinusFeeTarget && this.refundTypeMinusFeeTarget.checked
            ? "minus_fee"
            : "partial",
          refundAmount: this.refundAmountTarget.value,
          cancelUpcomingLessons: this.cancelUpcomingLessonsTarget.checked,
          refundReason: this.refundReasonTarget.value,
          paymentId: this.selectedPaymentId(),
          forceRefundWithoutLesson:
            this.hasForceRefundWithoutLessonTarget &&
            this.forceRefundWithoutLessonTarget.checked,
        }),
      }
    );

    if (response.ok) {
      if (document.getElementById("school_pupils_packages_payments_v2")) {
        window.dispatchEvent(new CustomEvent("driving-school-student-payments-v2:refresh"));
      } else {
        const url = new URL(window.location.href);
        url.searchParams.set("processing_refund", "true");
        window.location.href = url.toString();
      }
    } else {
      // get the message from the json response
      const jsonResponse = await response.json();
      alert(jsonResponse.error || "There was an error while trying to load the quick look, please try again later.");
    }

    this.closeQuickLook(event);
  }

  validateForm() {
    const validate = this.data.get("validate");
    if (!validate) {
      return;
    }

    const refundTypeFull = this.refundTypeFullTarget.checked;
    const refundTypeMinusFee = this.hasRefundTypeMinusFeeTarget && this.refundTypeMinusFeeTarget.checked;
    const refundTypePartial = this.refundTypePartialTarget.checked;
    const priceInPence = this.selectedMaxRefundInPence();
    const refundAmount = this.refundAmountTarget.value;
    const cancelUpcomingLessons = this.cancelUpcomingLessonsTarget.checked;
    const forceRefundWithoutLesson =
      this.hasForceRefundWithoutLessonTarget &&
      this.forceRefundWithoutLessonTarget.checked;

    console.log(
      JSON.stringify(
        {
          refundTypeFull,
          refundTypeMinusFee,
          refundTypePartial,
          priceInPence,
          refundAmount,
          cancelUpcomingLessons,
          forceRefundWithoutLesson,
        },
        null,
        2
      )
    );

    const errors = [];

    if (this.hasForceRefundWithoutLessonTarget && !forceRefundWithoutLesson) {
      errors.push(
        "Tick “Force refund if no lesson exists” to refund this payment without a linked lesson."
      );
    }

    // Check the refund amount is a vlid number, with no decimal places or 2 decimal places
    if (
      refundTypePartial &&
      !(refundAmount.match(/^\d+$/) || refundAmount.match(/^\d+\.\d{2}$/))
    ) {
      errors.push("Refund amount cannot be 0 or a decimal with more than 2 numbers.");
    }

    if (refundTypePartial && refundAmount * 100 >= priceInPence) {
      errors.push("Partial refund amount must be less than the remaining refundable amount");
    }

    if (errors.length === 0) {
      this.errorsTarget.hidden = true;
      this.errorsTarget.innerHTML = "";
      this.refundButtonTarget.disabled = false;
    } else {
      this.errorsTarget.hidden = false;
      this.errorsTarget.innerHTML = errors.join("<br>");
      this.refundButtonTarget.disabled = true;
    }

    this.enableRefundAmountIfApplicable();
  }

  enableRefundAmountIfApplicable() {
    const isPartial = this.refundTypePartialTarget.checked;
    this.refundAmountContainerTarget.hidden = !isPartial;
    this.refundAmountTarget.disabled = !isPartial;
  }

  selectedPaymentId() {
    if (!this.hasPaymentOptionTarget) {
      return null;
    }
    const selected = this.paymentOptionTargets.find((option) => option.checked);
    return selected ? selected.value : null;
  }

  selectedMaxRefundInPence() {
    const defaultPriceInPence = parseInt(this.data.get("priceInPence"), 10) || 0;
    if (!this.hasPaymentOptionTarget) {
      return defaultPriceInPence;
    }
    const selected = this.paymentOptionTargets.find((option) => option.checked);
    if (!selected) {
      return defaultPriceInPence;
    }
    return parseInt(selected.dataset.refundableAmountInPence, 10) || defaultPriceInPence;
  }

  beginButtonLoading() {
    let button = this.refundButtonTarget;
    let spinner = button.querySelector(".Spinner");
    spinner.classList.remove("u-hidden");
    spinner.classList.add("u-inlineBlock");
    button.disabled = true;
  }

  endButtonLoading() {
    let button = this.refundButtonTarget;
    let spinner = button.querySelector(".Spinner");
    spinner.classList.add("u-hidden");
    spinner.classList.remove("u-inlineBlock");
    button.disabled = false;
  }

  alert() {
    alert("Hello from the controller");
  }
}
